import React, { useEffect, useState } from 'react'

interface UsageData {
    totals: {
        prompt: number
        completion: number
        total: number
    }
    daily: {
        usage_date: string
        prompt_tokens: number
        completion_tokens: number
        total_tokens: number
    }[]
}

export default function Usage() {
    const [data, setData] = useState<UsageData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetch('/api/usage')
            .then(res => res.json())
            .then((res: any) => {
                if (res.error) throw new Error(res.error)
                setData(res as UsageData)
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false))
    }, [])

    if (loading) return <div className="card text-center text-secondary py-xl animate-pulse">Loading API Usage...</div>
    if (error) return <div className="badge badge-error">Failed to load usage: {error}</div>
    if (!data) return null

    // Format number with commas
    const fmt = (n: number) => new Intl.NumberFormat().format(n || 0)

    // Gemini Free Tier Limits
    const RPD_LIMIT = 1500
    const TPM_LIMIT = 1000000

    return (
        <div className="animate-fade-in" style={{ paddingBottom: '40px' }}>
            <div className="flex justify-between items-center mb-lg border-b pb-md" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <h2>API Token Usage</h2>
            </div>

            <div className="card mb-lg" style={{ borderLeft: '4px solid var(--brand-primary)' }}>
                <h3 className="mb-sm">Lifetime Token Usage</h3>
                <div className="grid grid-cols-3 gap-md mt-md">
                    <div>
                        <div className="text-sm text-secondary">Total Tokens</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-primary)' }}>{fmt(data.totals.total)}</div>
                    </div>
                    <div>
                        <div className="text-sm text-secondary">Prompt Tokens</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 600 }}>{fmt(data.totals.prompt)}</div>
                    </div>
                    <div>
                        <div className="text-sm text-secondary">Completion Tokens</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 600 }}>{fmt(data.totals.completion)}</div>
                    </div>
                </div>
            </div>

            <div className="card mb-lg">
                <h3 className="mb-sm">Gemini 2.5 Flash Lite Limits (Free Tier)</h3>
                <ul className="text-secondary text-sm" style={{ paddingLeft: '20px', lineHeight: 1.8 }}>
                    <li><strong>Requests Per Day:</strong> {fmt(RPD_LIMIT)} (Global)</li>
                    <li><strong>Tokens Per Minute:</strong> {fmt(TPM_LIMIT)}</li>
                    <li><strong>Requests Per Minute:</strong> 15</li>
                </ul>
                <div className="text-sm text-muted mt-sm pt-sm" style={{ borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
                    Note: If you hit the 15 requests per minute limit, the system gracefully sleeps and retries for you using exponential backoff.
                </div>
            </div>

            <h3 className="mb-md">Daily Breakdown (Last 30 Days)</h3>

            <div className="flex-col gap-sm">
                {data.daily.length === 0 ? (
                    <div className="text-secondary text-sm">No usage data recorded yet.</div>
                ) : (
                    data.daily.map(day => (
                        <div key={day.usage_date} className="card flex justify-between items-center">
                            <div style={{ fontWeight: 600 }}>{day.usage_date}</div>
                            <div className="flex gap-lg text-sm text-secondary">
                                <span title="Prompt Tokens">Prompt: {fmt(day.prompt_tokens)}</span>
                                <span title="Completion Tokens">Comp: {fmt(day.completion_tokens)}</span>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Total: {fmt(day.total_tokens)}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}
